'use client';

import { PageHeader as SharedPageHeader } from '@routly/ui';
import { Link, useRouterState } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { entryFor } from '@/components/layout/navItems';

/**
 * The title block, which is `@routly/ui`'s — the console used to draw its own,
 * 24px bold with the description underneath, against the product's 17px
 * semibold with the description beside it.
 *
 * What stays here is the half that knows this app: the glyph comes from the
 * rail's entry for the current route, and the back link is this router's.
 */

interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  /** Overrides the glyph the rail gives this route. */
  icon?: LucideIcon;
  /** Renders a back link above the title. Omit on top-level pages. */
  backTo?: string;
  backLabel?: string;
  actions?: ReactNode;
}

export function PageHeader({
  title,
  description,
  icon,
  backTo,
  backLabel = 'Back',
  actions,
}: PageHeaderProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return (
    <SharedPageHeader
      title={title}
      description={description}
      icon={icon ?? entryFor(pathname)?.icon}
      actions={actions}
      back={
        backTo ? (
          <Link
            to={backTo}
            aria-label={backLabel}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" /> {backLabel}
          </Link>
        ) : null
      }
    />
  );
}
